import { useState } from "react";
import type { FormEvent } from "react";

import { ApiError, uploadDocument } from "@/api/client";
import { REFERENCE_KINDS } from "@/screens/knowledge/kinds";
import type { ReferenceKind } from "@/screens/knowledge/kinds";
import { Button } from "@/ui/Button";
import { Dialog } from "@/ui/Dialog";
import { Select } from "@/ui/Input";
import { Notice } from "@/ui/Notice";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdded: () => void;
}

/** Upload one piece of past work: the file and what it is. A past take-off is read and
 * learned from as soon as it arrives. */
export function AddSourceDialog({ open, onOpenChange, onAdded }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState<ReferenceKind>("prior_takeoff");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chosen = REFERENCE_KINDS.find((k) => k.kind === kind)!;

  const reset = () => {
    setFile(null);
    setKind("prior_takeoff");
    setError(null);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await uploadDocument(file, { kind });
      reset();
      onOpenChange(false);
      onAdded();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "The upload did not reach the server.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      label="Add a source"
    >
      <form className="space-y-4 p-4 text-sm" onSubmit={(event) => void submit(event)}>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <label className="block space-y-1.5">
          <span className="font-medium text-ink">PDF</span>
          <input
            type="file"
            accept="application/pdf,.pdf"
            className="block w-full text-sm text-ink-2 file:mr-3 file:rounded-md file:border file:border-line-strong file:bg-surface file:px-3 file:py-1.5 file:text-ink"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
        </label>
        <label className="block space-y-1.5">
          <span className="font-medium text-ink">What it is</span>
          <Select value={kind} onChange={(event) => setKind(event.target.value as ReferenceKind)}>
            {REFERENCE_KINDS.map((k) => (
              <option key={k.kind} value={k.kind}>
                {k.label}
              </option>
            ))}
          </Select>
        </label>
        <p className="-mt-2 text-xs text-muted">{chosen.hint}</p>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!file || busy}>
            Add
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
